/**
 * Guided Experience Overlay
 *
 * Main component that renders:
 * - Spotlight overlay with cutout
 * - Positioned tooltip/checklist
 * - Intro modal
 * - Completion modal with celebration
 *
 * AGNOSTIC DESIGN:
 * - Renders based on step.step_type from API
 * - Supports: spotlight, checklist, info, celebration
 * - All text comes from database
 */

import React, { useEffect, useState, useId } from 'react';
import { createPortal } from 'react-dom';
import {
  X,
  ChevronRight,
  SkipForward,
  CheckCircle2,
  Circle,
  Sparkles,
  Lightbulb,
  Target,
  Award,
  ArrowRight,
} from 'lucide-react';
import { useGuidedExperience } from './GuidedExperienceProvider';

// ============================================================================
// SPOTLIGHT OVERLAY - Dark overlay with cutout using SVG mask
// PURELY VISUAL - pointer-events: none so clicks pass through to actual elements
// ============================================================================
function SpotlightOverlay({ rect, maskId }) {
  if (!rect) return null;

  const borderRadius = rect.shape === 'circle' ? rect.width / 2 : rect.shape === 'rounded' ? 8 : 0;

  return (
    <div className="fixed inset-0 z-[100] pointer-events-none">
      {/* SVG overlay with cutout - NO pointer events, purely visual */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none">
        <defs>
          <mask id={maskId}>
            {/* White = visible (dark overlay shows), Black = hidden (cutout) */}
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            <rect
              x={rect.left}
              y={rect.top}
              width={rect.width}
              height={rect.height}
              rx={borderRadius}
              ry={borderRadius}
              fill="black"
            />
          </mask>
        </defs>
        {/* Dark overlay with mask cutout - visual only */}
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="rgba(0,0,0,0.75)"
          mask={`url(#${maskId})`}
        />
      </svg>

      {/* Highlight ring around the cutout - decorative only */}
      <div
        className="absolute transition-all duration-300 pointer-events-none animate-pulse"
        style={{
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
          borderRadius: rect.shape === 'circle' ? '50%' : rect.shape === 'rounded' ? '8px' : '0',
          boxShadow: `0 0 0 4px ${rect.color}, 0 0 20px ${rect.color}`,
        }}
      />
    </div>
  );
}

// ============================================================================
// GUIDED TOOLTIP - Positioned tooltip with content
// ============================================================================
function GuidedTooltip({
  step,
  rect,
  onAcknowledge,
  onComplete,
  onSkip,
  onQuit,
  stepNumber,
  totalSteps,
  isMinimized,
  onRestore,
}) {
  if (!step || !rect) return null;

  // Calculate tooltip position
  const position = step.tooltip_position || 'bottom';
  const tooltipStyle = getTooltipPosition(position, rect);

  // For 'info' and 'checklist' steps, the acknowledge button should complete the step
  // For 'spotlight' steps, it just minimizes (user needs to click the highlighted element)
  const isInfoStep = step.step_type === 'info' || step.step_type === 'checklist';
  const handleAcknowledge = isInfoStep ? onComplete : onAcknowledge;
  const buttonLabel = isInfoStep ? 'Compris !' : "J'ai compris";

  // Minimized state - just show a small floating indicator
  if (isMinimized) {
    return (
      <button
        onClick={onRestore}
        className="fixed z-[110] flex items-center gap-2 px-3 py-2 bg-amber-500 hover:bg-amber-400 text-slate-900 text-sm font-medium rounded-full shadow-lg transition-colors animate-pulse"
        style={{ top: rect.top + rect.height / 2 - 16, left: rect.left + rect.width + 16 }}
        title="Afficher les instructions"
      >
        <Target size={16} />
        <span>
          {stepNumber}/{totalSteps}
        </span>
      </button>
    );
  }

  // Parse tips if it's a JSON string
  let tipsArray = [];
  if (step.tips) {
    try {
      tipsArray = typeof step.tips === 'string' ? JSON.parse(step.tips) : step.tips;
    } catch {
      tipsArray = [step.tips];
    }
  }

  return (
    <div
      className="fixed z-[110] w-[calc(100vw-2rem)] sm:w-80 max-w-sm bg-slate-800 rounded-xl shadow-2xl border border-slate-700 overflow-hidden animate-fade-in"
      style={tooltipStyle}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-slate-700/50 border-b border-slate-600">
        <div className="flex items-center gap-2">
          <Target className="w-5 h-5 text-amber-400" />
          <span className="font-medium text-white text-sm">{step.title}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-400">
            {stepNumber} / {totalSteps}
          </span>
          <button
            onClick={onQuit}
            className="p-1 text-slate-400 hover:text-white hover:bg-slate-600 rounded transition-colors"
            title="Quitter le guide"
          >
            <X size={14} />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <p className="text-sm text-slate-300 mb-3">{step.content}</p>

        {/* Tips - show as list if array */}
        {tipsArray.length > 0 && (
          <div className="p-2 bg-amber-500/10 rounded-lg mb-3 max-h-32 overflow-y-auto">
            <div className="flex items-start gap-2 mb-1">
              <Lightbulb className="w-4 h-4 text-amber-400 flex-shrink-0" />
              <span className="text-xs text-amber-300 font-medium">Conseils</span>
            </div>
            <ul className="ml-6 space-y-1">
              {tipsArray.map((tip, i) => (
                <li key={i} className="text-xs text-amber-200">
                  {tip}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Action hint - only show for spotlight steps (not info/checklist) */}
        {!isInfoStep && (
          <div className="flex items-start gap-2 p-2 bg-emerald-500/10 rounded-lg mb-3 border border-emerald-500/20">
            <ArrowRight className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-emerald-200">
              Cliquez sur l'element surligné pour continuer
            </p>
          </div>
        )}

        {/* Checklist (for checklist step type) */}
        {step.step_type === 'checklist' && step.checklist_items && (
          <ChecklistItems items={step.checklist_items} />
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between px-4 py-3 bg-slate-700/30 border-t border-slate-600">
        <div className="flex items-center gap-2">
          {step.can_skip && (
            <button
              onClick={onSkip}
              className="flex items-center gap-1 text-xs text-slate-400 hover:text-white transition-colors"
            >
              <SkipForward size={14} />
              Passer
            </button>
          )}
        </div>
        <button
          onClick={handleAcknowledge}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
            isInfoStep
              ? 'bg-amber-500 hover:bg-amber-400 text-slate-900'
              : 'bg-slate-600 hover:bg-slate-500 text-white'
          }`}
        >
          {buttonLabel}
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}

// Calculate tooltip position based on target rect
// Responsive: uses smaller dimensions on mobile screens
function getTooltipPosition(position, rect) {
  const offset = 16;
  const isMobile = window.innerWidth < 640; // Tailwind 'sm' breakpoint
  const tooltipWidth = isMobile ? Math.min(280, window.innerWidth - 32) : 320;
  const tooltipHeight = isMobile ? 180 : 200; // Approximate
  const edgePadding = isMobile ? 8 : 16;

  switch (position) {
    case 'top':
      return {
        bottom: window.innerHeight - rect.top + offset,
        left: Math.max(
          edgePadding,
          Math.min(
            rect.left + rect.width / 2 - tooltipWidth / 2,
            window.innerWidth - tooltipWidth - edgePadding
          )
        ),
      };
    case 'bottom':
      return {
        top: rect.top + rect.height + offset,
        left: Math.max(
          edgePadding,
          Math.min(
            rect.left + rect.width / 2 - tooltipWidth / 2,
            window.innerWidth - tooltipWidth - edgePadding
          )
        ),
      };
    case 'left':
      return {
        top: Math.max(edgePadding, rect.top + rect.height / 2 - tooltipHeight / 2),
        right: window.innerWidth - rect.left + offset,
      };
    case 'right':
    default:
      return {
        top: Math.max(edgePadding, rect.top + rect.height / 2 - tooltipHeight / 2),
        left: rect.left + rect.width + offset,
      };
  }
}

// ============================================================================
// CHECKLIST ITEMS - For checklist step type with completion tracking
// ============================================================================
function ChecklistItems({ items }) {
  const [completedItems, setCompletedItems] = React.useState(new Set());

  // Reset completed items when items change (new step)
  React.useEffect(() => {
    setCompletedItems(new Set());
  }, [items]);

  const toggleItem = index => {
    setCompletedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  return (
    <div className="space-y-2 mb-3">
      {items.map((item, i) => {
        const isCompleted = completedItems.has(i);
        return (
          <button
            key={i}
            onClick={() => toggleItem(i)}
            className="w-full flex items-center gap-2 text-left hover:bg-slate-700/30 p-1 -ml-1 rounded transition-colors"
          >
            {isCompleted ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
            ) : (
              <Circle className="w-4 h-4 text-slate-500 flex-shrink-0" />
            )}
            <span
              className={`text-sm transition-colors ${
                isCompleted
                  ? 'text-slate-500 line-through'
                  : item.mandatory
                    ? 'text-white'
                    : 'text-slate-400'
              }`}
            >
              {item.label}
              {item.mandatory && !isCompleted && <span className="text-red-400 ml-1">*</span>}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ============================================================================
// INTRO MODAL - Workflow introduction
// ============================================================================
function WorkflowIntroModal({ workflow, onBegin, onSkip }) {
  if (!workflow) return null;

  // Parse tips if string (with try-catch to prevent crashes)
  let tips = [];
  if (workflow.intro_tips) {
    try {
      tips =
        typeof workflow.intro_tips === 'string'
          ? JSON.parse(workflow.intro_tips)
          : workflow.intro_tips;
    } catch {
      // If parsing fails, treat as single tip string
      tips = [workflow.intro_tips];
    }
  }

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-md bg-slate-800 rounded-2xl shadow-2xl border border-slate-700 overflow-hidden animate-scale-in relative">
        {/* Close button */}
        <button
          onClick={onSkip}
          className="absolute top-3 right-3 z-10 p-1.5 text-white/60 hover:text-white hover:bg-white/10 rounded-full transition-colors"
          aria-label="Fermer"
        >
          <X size={20} />
        </button>

        {/* Header */}
        <div className="relative h-24 sm:h-32 bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
          <Sparkles className="w-16 h-16 text-white/90" />
          <div className="absolute inset-0 bg-[url('data:image/svg+xml,...')] opacity-10" />
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6">
          <h2 className="text-lg sm:text-xl font-bold text-white mb-2">{workflow.intro_title}</h2>
          <p className="text-sm sm:text-base text-slate-300 mb-4">{workflow.intro_message}</p>

          {/* Tips */}
          {tips.length > 0 && (
            <div className="space-y-2 mb-4 sm:mb-6">
              {tips.map((tip, i) => (
                <div key={i} className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 sm:w-5 h-4 sm:h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span className="text-xs sm:text-sm text-slate-300">{tip}</span>
                </div>
              ))}
            </div>
          )}

          {/* Estimated time */}
          {workflow.estimated_minutes && (
            <p className="text-xs text-slate-500 mb-4">
              Duree estimee: ~{workflow.estimated_minutes} minutes
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 bg-slate-700/30 border-t border-slate-600">
          <button
            onClick={onSkip}
            className="text-sm text-slate-400 hover:text-white transition-colors"
          >
            Pas maintenant
          </button>
          <button
            onClick={onBegin}
            className="flex items-center gap-2 px-6 py-2.5 bg-amber-500 hover:bg-amber-400 text-slate-900 font-medium rounded-lg transition-colors"
          >
            Commencer
            <ArrowRight size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// COMPLETION MODAL - Celebration with confetti
// ============================================================================
function WorkflowCompletionModal({ workflow, onClose }) {
  const [showConfetti, setShowConfetti] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setShowConfetti(false), 3000);
    return () => clearTimeout(timer);
  }, []);

  if (!workflow) return null;

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 p-4">
      {/* Simple confetti effect */}
      {showConfetti && <ConfettiEffect />}

      <div className="w-full max-w-md bg-slate-800 rounded-2xl shadow-2xl border border-slate-700 overflow-hidden animate-scale-in">
        {/* Header */}
        <div className="relative h-24 sm:h-32 bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
          <Award className="w-12 sm:w-16 h-12 sm:h-16 text-white/90" />
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 text-center">
          <h2 className="text-lg sm:text-xl font-bold text-white mb-2">
            {workflow.completion_title}
          </h2>
          <p className="text-sm sm:text-base text-slate-300 mb-4 sm:mb-6">
            {workflow.completion_message}
          </p>

          <button
            onClick={onClose}
            className="px-6 sm:px-8 py-2 sm:py-2.5 bg-emerald-500 hover:bg-emerald-400 text-white font-medium rounded-lg transition-colors"
          >
            Terminer
          </button>
        </div>
      </div>
    </div>
  );
}

// Simple confetti effect
function ConfettiEffect() {
  const colors = ['#fbbf24', '#34d399', '#60a5fa', '#f472b6', '#a78bfa'];
  const confetti = Array.from({ length: 50 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    delay: Math.random() * 0.5,
    color: colors[Math.floor(Math.random() * colors.length)],
    size: 8 + Math.random() * 8,
  }));

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-[130]">
      {confetti.map(c => (
        <div
          key={c.id}
          className="absolute animate-confetti-fall"
          style={{
            left: `${c.left}%`,
            top: -20,
            width: c.size,
            height: c.size,
            backgroundColor: c.color,
            borderRadius: Math.random() > 0.5 ? '50%' : '2px',
            animationDelay: `${c.delay}s`,
          }}
        />
      ))}
    </div>
  );
}

// ============================================================================
// ERROR TOAST - Shows error feedback to user
// ============================================================================
function ErrorToast({ message, onClose }) {
  if (!message) return null;

  return (
    <div className="fixed bottom-20 right-4 z-[150] max-w-sm animate-fade-in">
      <div className="flex items-center gap-3 px-4 py-3 bg-red-900/90 border border-red-700 rounded-lg shadow-lg">
        <div className="flex-shrink-0">
          <X className="w-5 h-5 text-red-400" />
        </div>
        <p className="text-sm text-red-100">{message}</p>
        <button
          onClick={onClose}
          className="flex-shrink-0 p-1 text-red-400 hover:text-red-200 transition-colors"
          aria-label="Fermer"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// MAIN OVERLAY COMPONENT
// ============================================================================
export function GuidedExperienceOverlay() {
  const {
    activeWorkflow,
    activeStep,
    spotlightRect,
    tooltipMinimized,
    isIntroOpen,
    isCompletionOpen,
    beginWorkflow,
    skipWorkflow,
    completeStep, // Used for info/celebration steps only
    skipStep,
    closeCompletion,
    minimizeTooltip,
    restoreTooltip,
    error,
    clearError,
  } = useGuidedExperience();

  // Generate unique mask ID to prevent SVG mask ID collision when multiple overlays render
  const maskId = useId().replace(/:/g, '-') + '-spotlight-mask';

  // Calculate step number and total
  const stepNumber = activeStep?.step_order || 1;
  const totalSteps = activeWorkflow?.steps?.length || 1;

  // Render modals and overlays through portal
  return createPortal(
    <>
      {/* Intro Modal */}
      {isIntroOpen && (
        <WorkflowIntroModal
          workflow={activeWorkflow}
          onBegin={beginWorkflow}
          onSkip={skipWorkflow}
        />
      )}

      {/* Completion Modal */}
      {isCompletionOpen && (
        <WorkflowCompletionModal workflow={activeWorkflow} onClose={closeCompletion} />
      )}

      {/* Active step overlay */}
      {activeStep && spotlightRect && (
        <>
          <SpotlightOverlay rect={spotlightRect} maskId={maskId} />
          <GuidedTooltip
            step={activeStep}
            rect={spotlightRect}
            onAcknowledge={minimizeTooltip}
            onComplete={completeStep}
            onSkip={skipStep}
            onQuit={skipWorkflow}
            stepNumber={stepNumber}
            totalSteps={totalSteps}
            isMinimized={tooltipMinimized}
            onRestore={restoreTooltip}
          />
        </>
      )}

      {/* Info step (no spotlight, just centered modal) - don't show if completion modal is open */}
      {activeStep && activeStep.step_type === 'info' && !spotlightRect && !isCompletionOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md bg-slate-800 rounded-xl shadow-2xl border border-slate-700 p-4 sm:p-6">
            <h3 className="text-lg font-medium text-white mb-2">{activeStep.title}</h3>
            <p className="text-slate-300 mb-4">{activeStep.content}</p>
            {activeStep.tips && (
              <div className="flex items-start gap-2 p-3 bg-amber-500/10 rounded-lg mb-4">
                <Lightbulb className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-200">{activeStep.tips}</p>
              </div>
            )}
            <div className="flex justify-end gap-3">
              {activeStep.can_skip && (
                <button
                  onClick={skipStep}
                  className="px-4 py-2 text-slate-400 hover:text-white transition-colors"
                >
                  {activeStep.skip_label || 'Passer'}
                </button>
              )}
              <button
                onClick={completeStep}
                className="px-4 py-2 bg-amber-500 hover:bg-amber-400 text-slate-900 font-medium rounded-lg transition-colors"
              >
                {activeStep.action_label || 'Continuer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Celebration step (no spotlight, confetti) - don't show if completion modal is open */}
      {activeStep && activeStep.step_type === 'celebration' && !isCompletionOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
          <ConfettiEffect />
          <div className="w-full max-w-md bg-slate-800 rounded-xl shadow-2xl border border-slate-700 p-4 sm:p-6 text-center">
            <Award className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">{activeStep.title}</h3>
            <p className="text-slate-300 mb-6">{activeStep.content}</p>
            <button
              onClick={completeStep}
              className="px-6 py-2.5 bg-emerald-500 hover:bg-emerald-400 text-white font-medium rounded-lg transition-colors"
            >
              {activeStep.action_label || 'Terminer'}
            </button>
          </div>
        </div>
      )}

      {/* Error toast for user feedback */}
      <ErrorToast message={error} onClose={clearError} />
    </>,
    document.body
  );
}

export default GuidedExperienceOverlay;
